'use client'

import { AlertCircle, Check, Lock, Mail } from 'lucide-react'
import Image from 'next/image'
import { useRouter, useSearchParams } from 'next/navigation'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import { authClient } from '~/lib/auth.client'

type LoginState = 'email' | 'sent'

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/.test(email)
}

export default function QuestionsLoginPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get('callbackUrl') ?? '/questions'

  const [state, setState] = useState<LoginState>('email')
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const emailValid = useMemo(() => isValidEmail(email), [email])

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!email.trim()) {
      toast.error('Введите email')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const checkResponse = await fetch('/api/email/verify-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      })

      const checkData = await checkResponse.json()

      if (!checkData.hasAccess) {
        const errorMessage = 'Email не найден в списке доступа'
        setError(errorMessage)
        toast.error(errorMessage, {
          description: 'База доступна только для учеников школы. Если вы ученик - напишите в поддержку.',
        })
        return
      }

      const result = await authClient.signIn.magicLink({
        email: email.trim(),
        callbackURL: callbackUrl,
      })

      if (result.error) {
        const errorMessage = result.error.message ?? 'Не удалось отправить ссылку'
        setError(errorMessage)
        toast.error(errorMessage)
        return
      }

      setState('sent')
      toast.success('Ссылка отправлена на вашу почту')
    }
    catch (err) {
      console.error('Magic link error:', err)
      const errorMessage = 'Произошла ошибка при отправке ссылки'
      setError(errorMessage)
      toast.error(errorMessage)
    }
    finally {
      setIsLoading(false)
    }
  }

  function handleResend() {
    setState('email')
  }

  if (state === 'sent') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className={`
              mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-green-100
              dark:bg-green-900/30
            `}
            >
              <Mail className="size-8 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-2xl">Проверьте почту</CardTitle>
            <CardDescription className="text-base">
              Мы отправили ссылку для входа на
              {' '}
              <span className="font-medium text-foreground">{email}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-center text-sm text-muted-foreground">
              Ссылка действительна 5 минут. Если письмо не пришло, проверьте папку «Спам».
            </p>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleResend}
            >
              Отправить повторно
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => router.push('/')}
            >
              Вернуться на главную
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <Card className="w-full max-w-md overflow-hidden">
        <CardHeader className="text-center">
          <div className={`
            mx-auto mb-4 flex size-16 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br
            from-primary/20 to-primary/5
          `}
          >
            <Image
              src="/assets/vadim-novosolov.jpg"
              alt="Gernar"
              width={64}
              height={64}
              className="size-full object-cover"
            />
          </div>
          <CardTitle className="text-2xl">Вход на платформу</CardTitle>
          <CardDescription className="text-base">
            База вопросов для учеников Gernar
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  placeholder="Введите ваш email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value)
                    if (error)
                      setError(null)
                  }}
                  disabled={isLoading}
                  autoFocus
                  required
                  className={`
                    h-12 pr-10 text-base transition-all
                    ${error ? 'border-red-500 focus-visible:ring-red-500/20' : ''}
                    ${emailValid && !error ? 'border-green-500 focus-visible:ring-green-500/20' : ''}
                  `}
                />
                {error && (
                  <div className="absolute top-1/2 right-3 -translate-y-1/2">
                    <AlertCircle className="size-5 text-red-500" />
                  </div>
                )}
                {emailValid && !error && (
                  <div className={`
                    absolute top-1/2 right-3 flex size-5 -translate-y-1/2 items-center justify-center rounded-full
                    bg-green-500
                  `}
                  >
                    <Check className="size-3 text-white" />
                  </div>
                )}
              </div>
              {error && (
                <p className="text-center text-sm text-red-500">
                  {error}
                </p>
              )}
            </div>
            <p className="text-center text-xs text-muted-foreground">
              Ссылка для входа придёт на этот адрес
            </p>
            <Button
              type="submit"
              className={`
                h-12 w-full bg-gradient-to-r from-primary to-primary/80 text-base font-medium transition-all
                duration-300
                hover:from-primary/90 hover:to-primary/70
              `}
              isLoading={isLoading}
              disabled={isLoading || !emailValid}
            >
              <Mail className="size-5" />
              Получить ссылку
            </Button>
          </form>

          <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="size-3" />
            <span>Мы не передаём данные третьим лицам</span>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
